<script setup lang="ts">
/**
 * Таблица фича-стендов — макет 263:1683: строка на стенд, шапка 34
 * моноширинная, действия справа иконками.
 *
 * Строка — это стенд целиком, а не окружение одного репозитория: namespace у
 * сервисов общий, и гасится он тоже целиком. Состав показан чипами: у кого
 * есть адрес — ссылка, у кого нет — приглушённый чип, выката ещё не было.
 */
import { ExternalLink, RefreshCw, Server, X } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

import { formatSpan, formatUptime, initials, shortService, standStateOf } from '../format'

import type { FeatureStand } from '../api'

defineProps<{ stands: FeatureStand[]; busyName?: string }>()

const emit = defineEmits<{ redeploy: [stand: FeatureStand]; drop: [stand: FeatureStand] }>()

const { t } = useI18n()

const uptimeLabels = () => ({
  days: t('devops.envs.uptime.days'),
  hours: t('devops.envs.uptime.hours'),
  minutes: t('devops.envs.uptime.minutes'),
})

/** Когда стенд снесёт себя сам — подсказкой на аптайме, если срок известен. */
const autoStopIn = (iso: string) => {
  if (!iso) return ''

  const left = new Date(iso).getTime() - Date.now()

  if (Number.isNaN(left) || left <= 0) return ''

  return t('devops.envs.autoStopIn', { time: formatSpan(left, uptimeLabels()) })
}
</script>

<template>
  <div class="stands">
    <div class="stands__head">
      <span class="stands__col stands__col--stand">{{ t('devops.envs.columns.stand') }}</span>
      <span class="stands__col stands__col--issue">{{ t('devops.envs.columns.issue') }}</span>
      <span class="stands__col stands__col--services">
        {{ t('devops.envs.columns.services') }}
      </span>
      <span class="stands__col stands__col--actor">{{ t('devops.envs.columns.actor') }}</span>
      <span class="stands__col stands__col--uptime">{{ t('devops.envs.columns.uptime') }}</span>
      <span class="stands__spacer" />
      <span class="stands__col">{{ t('devops.envs.columns.actions') }}</span>
    </div>

    <div v-for="stand in stands" :key="stand.name" class="stands__row">
      <Server class="stands__icon" :size="16" />
      <span class="stands__cell stands__cell--stand">{{ stand.name }}</span>
      <span class="stands__cell stands__cell--issue">
        <template v-if="stand.issueKey">
          <RouterLink class="stands__link" :to="`/issue/${stand.issueKey}`">
            {{ stand.issueKey }}
          </RouterLink>
          {{ stand.issueTitle }}
        </template>
        <span v-else class="stands__muted">{{ t('devops.envs.noIssue') }}</span>
      </span>

      <!-- Состав: адрес есть — чип ведёт на стенд, адреса нет — сервис только
           собрался, и вести по нему некуда. -->
      <span class="stands__cell stands__cell--services">
        <template v-for="service in stand.services" :key="service.projectPath">
          <a
            v-if="service.url"
            class="stands__chip stands__chip--link"
            :href="service.url"
            target="_blank"
            rel="noopener"
            :title="`${service.projectPath} · ${service.branch}`"
          >
            {{ shortService(service.projectPath) }}
            <ExternalLink :size="11" />
          </a>
          <span
            v-else
            class="stands__chip"
            :title="`${service.projectPath} · ${t('devops.envs.noUrl')}`"
          >
            {{ shortService(service.projectPath) }}
          </span>
        </template>
      </span>

      <span class="stands__cell stands__cell--actor" :title="stand.actor">
        {{ initials(stand.actor) }}
      </span>
      <span class="stands__cell stands__cell--uptime" :title="autoStopIn(stand.autoStopAt)">
        {{
          standStateOf(stand) === 'building'
            ? t('devops.envs.state.buildingUptime')
            : formatUptime(stand.raisedAt, uptimeLabels())
        }}
      </span>
      <span class="stands__badge" :class="`stands__badge--${standStateOf(stand)}`">
        {{ t(`devops.envs.state.${standStateOf(stand)}`) }}
      </span>

      <span class="stands__spacer" />

      <a
        v-if="stand.urls[0]"
        class="stands__action"
        :href="stand.urls[0]"
        target="_blank"
        rel="noopener"
        :aria-label="t('devops.envs.actions.open')"
        :title="t('devops.envs.actions.open')"
      >
        <ExternalLink :size="15" />
      </a>
      <button
        type="button"
        class="stands__action"
        :disabled="busyName === stand.name || !stand.issueId"
        :aria-label="t('devops.envs.actions.redeploy')"
        :title="
          stand.issueId ? t('devops.envs.actions.redeploy') : t('devops.envs.actions.noIssueHint')
        "
        @click="emit('redeploy', stand)"
      >
        <RefreshCw :size="15" />
      </button>
      <button
        type="button"
        class="stands__action stands__action--danger"
        :disabled="busyName === stand.name"
        :aria-label="t('devops.envs.actions.drop')"
        :title="t('devops.envs.actions.drop')"
        @click="emit('drop', stand)"
      >
        <X :size="15" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.stands {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background: var(--color-surface);
  overflow: hidden;
}

.stands__head,
.stands__row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
  border-bottom: 1px solid var(--color-border);
}

.stands__head {
  height: 34px;
  background: var(--color-bg-muted);
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
  letter-spacing: 0.04em;
}

.stands__row {
  height: 52px;
}

.stands__row:last-child {
  border-bottom: none;
}

.stands__icon {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.stands__col--stand,
.stands__cell--stand {
  width: 140px;
}

.stands__col--issue,
.stands__cell--issue {
  width: 220px;
}

.stands__col--services,
.stands__cell--services {
  width: 260px;
}

/* Шире подписи «Поднял»: иначе шапка слипается с соседней колонкой */
.stands__col--actor,
.stands__cell--actor {
  width: 56px;
}

.stands__col--uptime,
.stands__cell--uptime {
  width: 76px;
}

.stands__cell {
  overflow: hidden;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-body-sm);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.stands__cell--stand {
  color: var(--color-text-primary);
}

/* Задача — обычным шрифтом: это единственная колонка с человеческим текстом,
   и моноширинный делает её нечитаемой. */
.stands__cell--issue {
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: var(--font-size-body);
}

.stands__cell--services {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Чип состава — макет 263:1683: высота 20, радиус 6, моноширинный 11 */
.stands__chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 6px;
  border-radius: var(--radius-sm);
  background: var(--color-bg-muted);
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
  text-decoration: none;
  white-space: nowrap;
}

.stands__chip--link {
  color: var(--color-primary);
}

.stands__chip--link:hover {
  background: var(--color-primary-tint);
}

.stands__link {
  color: var(--color-primary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-body-sm);
  text-decoration: none;
}

.stands__link:hover {
  text-decoration: underline;
}

.stands__muted {
  color: var(--color-text-muted);
}

.stands__spacer {
  flex: 1;
  min-width: 0;
}

.stands__badge {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  border-radius: var(--radius-sm);
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
}

.stands__badge--active {
  background: var(--color-success-tint);
  color: var(--color-success-strong);
}

.stands__badge--building {
  background: var(--color-info-tint);
  color: var(--color-info-strong);
}

.stands__badge--idle {
  background: var(--color-warning-tint);
  color: var(--color-warning-strong);
}

.stands__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-muted);
  cursor: pointer;
}

.stands__action:hover {
  color: var(--color-text-primary);
}

.stands__action--danger:hover {
  color: var(--color-danger);
}

.stands__action:disabled {
  color: var(--color-text-disabled);
  cursor: default;
}
</style>
